'use client';
import { createContext, ReactNode, useContext, useState } from 'react';

interface AccordionProviderProps {
  children: ReactNode;
  isMultiMode?: boolean;
}

interface AccordionContextType {
  openItemId: string | null;
  setOpenItemId: (id: string | null) => void;
  openItemIds: string[]; // used only in multi-open mode
  toggleItemId: (id: string) => void;
  isMultiMode: boolean;
  getIsItemOpen: (id: string) => boolean;
  closeAll: () => void;
}

const AccordionContext = createContext<AccordionContextType | undefined>(
  undefined
);

export function useAccordion() {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error('useAccordion must be used within a AccordionProvider');
  }
  return context;
}

export function AccordionProvider({
  children,
  isMultiMode = false,
}: AccordionProviderProps) {
  const [openItemId, setOpenItemId] = useState<string | null>(
    isMultiMode ? null : '0'
  );

  const [openItemIds, setOpenItemIds] = useState<string[]>([]);

  const toggleItemId = (id: string) => {
    if (!isMultiMode) {
      setOpenItemId((current) => (current === id ? null : id));
    } else {
      setOpenItemIds((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      );
    }
  };

  const getIsItemOpen = (id: string) => {
    return isMultiMode ? openItemIds.includes(id) : openItemId === id;
  };

  const closeAll = () => {
    if (isMultiMode) setOpenItemIds([]);
    else setOpenItemId(null);
  };

  return (
    <AccordionContext.Provider
      value={{
        openItemId,
        setOpenItemId,
        openItemIds,
        toggleItemId,
        isMultiMode,
        getIsItemOpen,
        closeAll,
      }}
    >
      {children}
    </AccordionContext.Provider>
  );
}
